"use client";
import React, { useEffect, useState, useRef } from "react";
import CommonDialog from "../../../global/dialog";
import CommonTabs from "../../../global/tabs";
import CommonForm from "../../../global/form";
import { Form, Button, Typography, Input, message, Tag } from "antd";
import { CopyOutlined, CheckOutlined } from "@ant-design/icons";
import CommonInput from "../../../global/input";
import CommonCheckbox from "../../../global/checkbox";
import QRCode from "react-qr-code";
import Spinner from "../../../global/spinner";
import CommonTable from "../../../global/table";

import { useGenericQuery } from "../../../../queries/use-generic-query";
import { useGenericMutation } from "../../../../queries/use-generic-mutation";
import { QUERY_KEYS } from "../../../../lib/query-constants";
import { useQueryClient } from "@tanstack/react-query";
import SOURCES from "../../../../utils/constants";
import { cn } from "../../../../utils/cn";

const { Title, Text } = Typography;
const { TextArea } = Input;

const EnhanceAddRequestForm = ({ open, setOpen, editingId }) => {
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState("1");

    const [webFormUrl, setWebFormUrl] = useState("");
    const [iframeCode, setIframeCode] = useState("");
    const [htmlCode, setHtmlCode] = useState("");
    const [customFields, setCustomFields] = useState([]);
    const [copiedStates, setCopiedStates] = useState({ url: false, qr: false, iframe: false, html: false });

    const qrCodeRef = useRef(null);

    // --- Queries ---
    const { data: weblinkDetails, isLoading: isLoadingDetails, isFetching: isFetchingDetails } = useGenericQuery({
        queryKey: QUERY_KEYS.weblink.item(editingId),
        endpoint: "weblink/detail",
        queryParams: { id: editingId },
        enabled: !!editingId && open
    });

    const { data: conversateDestinations } = useGenericQuery({
        queryKey: QUERY_KEYS.conversate.all,
        endpoint: "integration?integrationCompany=conversate"
    });

    // --- Mutations ---
    const saveMutation = useGenericMutation({
        endpoint: "weblink",
        onSuccess: () => {
            message.success(editingId ? "Form updated successfully" : "Form created successfully");
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.weblink.lists() });
            if (editingId) queryClient.invalidateQueries({ queryKey: QUERY_KEYS.weblink.item(editingId) });
            setOpen(false);
            form.resetFields();
            setWebFormUrl("");
            setActiveTab("1");
        },
        onError: (err) => message.error(err?.response?.data?.error || "Error saving form")
    });

    // --- Effects ---
    useEffect(() => {
        if (!open) {
            setWebFormUrl("");
            setIframeCode("");
            setHtmlCode("");
            setCustomFields([]);
            form.resetFields();
            setActiveTab("1");
        }
    }, [open, form]);

    useEffect(() => {
        if (open && editingId && weblinkDetails) {
            const data = weblinkDetails.results || weblinkDetails;
            if (!data) return;

            const settings = data.settings || {};
            console.log("settings", settings);

            form.setFieldsValue({
                formName: data.formName,
                autoresponseDelay: data.autoresponseDelay,
                conversateInbox: settings.conversateInbox,
                conversateAgent: settings.conversateAgent,
                conversateType: settings.conversateType,
                conversateMessage: settings.conversateMessage,
                ...settings
            });

            setCustomFields(data.customFields || []);

            if (data.webformUrl || weblinkDetails.webformUrl) {
                const url = data.webformUrl || weblinkDetails.webformUrl;
                setWebFormUrl(url);
                setIframeCode(`<iframe width="100%" height="1000" frameBorder="0" src="${url}" />`);
                setHtmlCode(`<!-- HTML Form Code -->\n<form action="${url}" method="POST">...</form>`);
            }
        }
    }, [open, editingId, weblinkDetails, form]);

    // --- Form Fields ---
    const generalFormData = [
        { inputType: SOURCES.FORMINPUTTYPES.CUSTOM, component: <p className="text-lg font-medium">Basic Info</p>, span: 24, className: "h-7" },
        { label: "Form Name", name: "formName", inputType: SOURCES.FORMINPUTTYPES.INPUT, type: "text", placeholder: "Enter Form Name", required: true, message: "Form Name is required", span: 12 },
        { label: "Autoresponse Delay (Minutes)", name: "autoresponseDelay", inputType: SOURCES.FORMINPUTTYPES.INPUT, type: "number", placeholder: "0", required: true, message: "Delay is required", span: 12 },
        { inputType: SOURCES.FORMINPUTTYPES.CUSTOM, component: <p className="text-lg font-medium">Conversate Integration</p>, span: 24, className: "h-7" },
        { label: "Inbox", name: "conversateInbox", inputType: SOURCES.FORMINPUTTYPES.SELECT, placeholder: "Select Inbox", options: conversateDestinations?.inboxProvider?.map(i => ({ label: i.label, value: i.value })) || [], span: 12 },
        { label: "Agent", name: "conversateAgent", inputType: SOURCES.FORMINPUTTYPES.SELECT, placeholder: "Select Agent", options: conversateDestinations?.agentProvider?.map(a => ({ label: a.label, value: a.value })) || [], span: 12 },
        { label: "Type", name: "conversateType", inputType: SOURCES.FORMINPUTTYPES.SELECT, placeholder: "Select Type", options: [{ label: "Sms", value: "sms" }, { label: "Email", value: "email" }], span: 12 },
        { label: "Initial Message", name: "conversateMessage", inputType: SOURCES.FORMINPUTTYPES.INPUT, type: "textarea", placeholder: "Write initial message...", span: 24 }
    ];

    const copyToClipboard = (text, type) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedStates(prev => ({ ...prev, [type]: true }));
        setTimeout(() => setCopiedStates(prev => ({ ...prev, [type]: false })), 2000);
    };

    const handleCopyQrCode = async () => {
        try {
            if (!qrCodeRef.current) return;

            const svg = qrCodeRef.current;
            const svgData = new XMLSerializer().serializeToString(svg);

            // Convert SVG to Image via Blob
            const svgBlob = new Blob([svgData], { type: "image/svg+xml" });
            const svgUrl = URL.createObjectURL(svgBlob);

            const img = new Image();
            img.src = svgUrl;

            await new Promise((resolve, reject) => {
                img.onload = () => {
                    URL.revokeObjectURL(svgUrl); // cleanup
                    resolve();
                };
                img.onerror = reject;
            });

            // Draw image on canvas
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            if (!ctx) throw new Error("Canvas context not available");
            ctx.drawImage(img, 0, 0);

            // Convert canvas to Blob
            const blob = await new Promise((resolve) => {
                canvas.toBlob((b) => resolve(b), "image/png");
            });

            if (!blob) throw new Error("Failed to create blob from canvas");

            // Copy image to clipboard
            await navigator.clipboard.write([
                new ClipboardItem({ "image/png": blob })
            ]);

            // Feedback state
            setCopiedStates(prev => ({ ...prev, qr: true }));
            setTimeout(() => setCopiedStates(prev => ({ ...prev, qr: false })), 2000);

            console.log("QR code copied successfully!");
        } catch (err) {
            console.error("Failed to copy QR code:", err);
        }
    };



    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            console.log("onsubmit with values", values)
            await saveMutation.mutateAsync({ id: editingId, formName: values.formName, autoresponseDelay: values.autoresponseDelay, settings: values, customFields });
        } catch (err) { console.error(err); }
    };

    const fieldList = ["First Name", "Last Name", "Phone", "Email", "Event Name", "Event Date", "Event Type", "Budget", "Guest Count", "Service Address", "Company", "Notes / Inquiry"];

    const tableData = fieldList.map((field) => ({
        id: field,
        name: field,
        key: field,
    }));

    const columns = [
        {
            title: "Field Name",
            dataIndex: "name",
            key: "name",
            width: "50%",
        },
        {
            title: "Required",
            key: "required",
            align: "center",
            render: (_, record) => (
                <Form.Item name={`${record.name.replace(/ /g, "")}Required`} valuePropName="checked" noStyle>
                    <CommonCheckbox className="justify-center" />
                </Form.Item>
            ),
        },
        {
            title: "Hidden",
            key: "hidden",
            align: "center",
            render: (_, record) => (
                <Form.Item name={`${record.name.replace(/ /g, "")}Hidden`} valuePropName="checked" noStyle>
                    <CommonCheckbox className="justify-center" />
                </Form.Item>
            ),
        },
    ];

    const tabItems = [
        { key: "1", label: "General Settings", children: <CommonForm form={form} data={generalFormData} layoutType="vertical" /> },
        {
            key: "2", label: "Field Configuration", children: (
                <div className="pt-4">
                    <CommonTable
                        columns={columns}
                        data={tableData}
                        showActions={false}
                        pagination={false}
                    />
                </div>
            )
        }
    ];

    if (editingId && webFormUrl) {
        tabItems.push({
            key: "3", label: "Embed & Share", children: (
                <div className="pt-4 grid grid-cols-1 md:grid-cols-5 gap-6 h-full pb-2">
                    {/* Left Column: Public Link & QR Code */}
                    <div className="md:col-span-12 space-y-6">
                        {/* Public Link */}
                        <div className="p-4 border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow">
                            <h3 className="font-semibold text-gray-800 mb-2">Public Link</h3>
                            <p className="text-xs text-gray-500 mb-3">Share this link directly with your leads.</p>
                            <div className="flex items-center gap-3">
                                <input
                                    className="flex-1 pl-3 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 font-mono outline-none focus:border-primary transition-all"
                                    readOnly
                                    value={webFormUrl}
                                />
                                <Button
                                    type="primary"
                                    icon={copiedStates.url ? <CheckOutlined /> : <CopyOutlined />}
                                    onClick={() => copyToClipboard(webFormUrl, "url")}
                                    className={cn("h-10", copiedStates.url ? "!bg-green-500 hover:!bg-green-500" : "!bg-primary")}
                                >
                                    {copiedStates.url ? "Copied" : "Copy"}
                                </Button>
                            </div>
                        </div>

                        {/* QR Code */}
                        <div className="p-4 border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow">
                            <h3 className="font-semibold text-gray-800 mb-2">QR Code</h3>
                            <p className="text-xs text-gray-500 mb-3">Scan to open the form.</p>
                            <div className="p-3 bg-white border border-gray-100 rounded-xl shadow-sm mb-3 w-full flex justify-center">
                                <QRCode
                                    value={webFormUrl}
                                    size={20}
                                    ref={qrCodeRef}
                                    style={{ height: "auto", maxWidth: "100%", width: "150px" }}
                                    viewBox={`0 0 256 256`}
                                />
                            </div>
                            <Button
                                block
                                icon={copiedStates.qr ? <CheckOutlined /> : <CopyOutlined />}
                                onClick={handleCopyQrCode}
                                className={copiedStates.qr ? "!text-green-500 !border-green-500 !bg-green-50" : ""}
                            >
                                {copiedStates.qr ? "Copied" : "Copy QR Image"}
                            </Button>
                        </div>
                    </div>

                    {/* Right Column: Embed Code */}
                    <div className="flex items-center gap-x-6 space-y-6 col-span-12">
                        {/* iFrame */}
                        <div className="p-3 border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-center mb-2">
                                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-blue-500"></span> iFrame
                                </h4>
                                {/* <Button
                                    size="small"
                                    icon={copiedStates.iframe ? <CheckOutlined /> : <CopyOutlined />}
                                    onClick={() => copyToClipboard(iframeCode, "iframe")}
                                >
                                    {copiedStates.iframe ? "Copied" : "Copy"}
                                </Button> */}
                                <Button
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        copyToClipboard(iframeCode, "iframe");
                                    }}
                                    className={cn(
                                        "flex items-center hover:!border hover:!border-primary hover:!text-primary",
                                        copiedStates.iframe ? "!border-green-500 !text-green-500" : ""
                                    )}
                                >
                                    {copiedStates.iframe ? "Copied" : "Copy"}
                                </Button>
                            </div>
                            <TextArea
                                value={iframeCode}
                                rows={4}
                                readOnly
                                className="!bg-[#1e293b] !text-gray-300 !border-none rounded-md font-mono text-xs p-3 resize-none"
                            />
                        </div>

                        {/* Full HTML */}
                        <div className="p-3 border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow m-0">
                            <div className="flex justify-between items-center mb-2">
                                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-orange-500"></span> Full HTML
                                </h4>
                                <Button
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        copyToClipboard(htmlCode, "html");
                                    }}
                                    className={cn(
                                        "flex items-center hover:!border hover:!border-primary hover:!text-primary",
                                        copiedStates.html ? "!border-green-500 !text-green-500" : ""
                                    )}
                                >
                                    {copiedStates.html ? "Copied" : "Copy"}
                                </Button>
                            </div>
                            <TextArea
                                value={htmlCode}
                                rows={4}
                                readOnly
                                className="!bg-[#1e293b] !text-gray-300 !border-none rounded-md font-mono text-xs p-3 resize-none"
                            />
                        </div>
                    </div>
                </div>

            )
        });
    }

    return (
        <CommonDialog
            title={editingId ? "Edit Contact Request Form" : "New Contact Request Form"}
            description={editingId ? "Update your form details and settings." : "Create a new contact request form."}
            open={open}
            setOpen={setOpen}
            onOk={handleSubmit}
            onCancel={() => setOpen(false)}
            isConfirmButtonLoading={saveMutation.isPending}
            okButtonTitle={editingId ? "Update" : "Create"}
            width={900}
            contentClassName={(isLoadingDetails || isFetchingDetails) && editingId && "!overflow-hidden"}
        >
            {(isLoadingDetails || isFetchingDetails) && editingId && (
                <div className="absolute -top-2 left-0 w-full h-full bg-black/15 z-10 rounded-lg flex justify-center items-center">
                    <div className="flex flex-col justify-center items-center gap-1">
                        <div className="size-6 rounded-full border-2 border-white !border-t-primary animate-spin" />
                    </div>
                </div>
            )}
            <Form form={form} layout="vertical" onFinish={handleSubmit} className="space-y-6">
                <CommonTabs items={tabItems} activeKey={activeTab} onChange={setActiveTab} className="mt-4" />
            </Form>
        </CommonDialog>
    );
};

export default EnhanceAddRequestForm;
